// ============================================================================
// ENTITY TREE HEADER - Mode toggle, domain filters, and phase indicator
// ============================================================================

import type { EntityDomain } from '@ispot/shared'

export type DomainFilter = {
  market: boolean
  business: boolean
  solution: boolean
}

interface EntityTreeHeaderProps {
  mode: 'structured' | 'free'
  onModeChange: (mode: 'structured' | 'free') => void
  showModeToggle?: boolean
  currentPhaseName?: string
  phaseColor?: EntityDomain
  /** Domain filter state */
  domainFilter: DomainFilter
  /** Callback when domain filter changes */
  onDomainFilterChange: (filter: DomainFilter) => void
  /** Show domain filter chips */
  showDomainFilter?: boolean
}

export function EntityTreeHeader({
  mode,
  onModeChange,
  showModeToggle = true,
  currentPhaseName,
  phaseColor = 'market',
  domainFilter,
  onDomainFilterChange,
  showDomainFilter = true,
}: EntityTreeHeaderProps) {
  const domainColors = {
    market: 'bg-market',
    business: 'bg-business',
    solution: 'bg-solution',
  }

  const toggleDomain = (domain: EntityDomain) => {
    onDomainFilterChange({
      ...domainFilter,
      [domain]: !domainFilter[domain],
    })
  }

  return (
    <div className="border-b border-warm-200">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 bg-warm-100">
        <h2 className="font-display font-semibold text-base text-warm-800">
          Entity Tree
        </h2>

        <div className="flex items-center gap-3">
          {/* Domain filter chips */}
          {showDomainFilter && (
            <div className="flex gap-1">
              <button
                onClick={() => toggleDomain('market')}
                className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium rounded-full border transition-all ${
                  domainFilter.market
                    ? 'border-market bg-market/10 text-market'
                    : 'border-warm-200 bg-white text-warm-400 opacity-50'
                }`}
                title="Toggle Market domain"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-market" />
                M
              </button>
              <button
                onClick={() => toggleDomain('business')}
                className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium rounded-full border transition-all ${
                  domainFilter.business
                    ? 'border-business bg-business/10 text-business'
                    : 'border-warm-200 bg-white text-warm-400 opacity-50'
                }`}
                title="Toggle Business domain"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-business" />
                B
              </button>
              <button
                onClick={() => toggleDomain('solution')}
                className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium rounded-full border transition-all ${
                  domainFilter.solution
                    ? 'border-solution bg-solution/10 text-solution'
                    : 'border-warm-200 bg-white text-warm-400 opacity-50'
                }`}
                title="Toggle Solution domain"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-solution" />
                S
              </button>
            </div>
          )}

          {showModeToggle && (
            <div className="flex gap-1 p-0.5 bg-warm-200 rounded-md">
              <button
                onClick={() => onModeChange('structured')}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${
                  mode === 'structured'
                    ? 'bg-white text-warm-800 shadow-sm'
                    : 'text-warm-600 hover:text-warm-800'
                }`}
              >
                Structured
              </button>
              <button
                onClick={() => onModeChange('free')}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${
                  mode === 'free'
                    ? 'bg-white text-warm-800 shadow-sm'
                    : 'text-warm-600 hover:text-warm-800'
                }`}
              >
                Free
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Phase indicator (structured mode only) */}
      {mode === 'structured' && currentPhaseName && (
        <div className="flex items-center gap-2 px-4 py-2 bg-warm-50 text-xs text-warm-600">
          <span>Showing entities for:</span>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-warm-200 rounded-md font-medium">
            <span className={`w-2 h-2 rounded-full ${domainColors[phaseColor]}`} />
            <span>{currentPhaseName}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default EntityTreeHeader
